// VERA-246: print pre-flight for a placed custom logo. Self-serve placement
// removes the design-iteration round-trip, but not the need to catch prints that
// will come out badly — so we validate resolution, safe-area, and asset type up
// front. `errors` block ordering; `warnings` are advisory.

export interface LogoPreflightParams {
  isSvg: boolean
  naturalWidth: number
  naturalHeight: number
  /** Logo width as a % of the canvas (tag) width. */
  scalePct: number
  /** Logo centre as a % of canvas width/height. */
  xPct: number
  yPct: number
  shape: 'round' | 'rect'
  canvasWidthMm: number
  canvasHeightMm: number
}

export interface LogoPreflightResult {
  /** Blocking problems (prevent ordering). */
  errors: string[]
  /** Real quality risks at the current size/placement. */
  warnings: string[]
  /** Gentle, non-blocking suggestions (e.g. prefer a vector logo). */
  tips: string[]
  /** Effective print resolution at the current size (null for vector). */
  dpi: number | null
}

const MM_PER_INCH = 25.4
const MIN_DPI = 150 // below this the print is unacceptably soft → block
const GOOD_DPI = 300 // industry "photo quality" target → warn below
const SAFE_INSET = 0.06 // 6% trim/bleed margin the logo should stay inside

export const computeLogoPreflight = ({
  isSvg,
  naturalWidth,
  naturalHeight,
  scalePct,
  xPct,
  yPct,
  shape,
  canvasWidthMm,
  canvasHeightMm,
}: LogoPreflightParams): LogoPreflightResult => {
  const errors: string[] = []
  const warnings: string[] = []
  const tips: string[] = []

  const logoWidthMm = (scalePct / 100) * canvasWidthMm
  const aspect = naturalWidth > 0 && naturalHeight > 0 ? naturalWidth / naturalHeight : 1
  const logoHeightMm = logoWidthMm / aspect

  // ── Resolution (raster only — vector prints crisp at any size) ──
  let dpi: number | null = null
  if (!isSvg && logoWidthMm > 0) {
    dpi = Math.round(naturalWidth / (logoWidthMm / MM_PER_INCH))
    if (dpi < MIN_DPI) {
      errors.push(
        `Logo resolution is too low to print at this size (~${dpi} DPI). Upload a larger image or an SVG, or make the logo smaller.`,
      )
    } else if (dpi < GOOD_DPI) {
      warnings.push(
        `Logo is a bit low-res at this size (~${dpi} DPI). 300 DPI prints sharpest — a vector (SVG) avoids this entirely.`,
      )
    }
  }

  // ── Safe area (keep the logo clear of the trim edge) ──
  const fracW = logoWidthMm / canvasWidthMm
  const fracH = logoHeightMm / canvasHeightMm
  const cx = xPct / 100
  const cy = yPct / 100
  const left = cx - fracW / 2
  const right = cx + fracW / 2
  const top = cy - fracH / 2
  const bottom = cy + fracH / 2

  if (shape === 'round') {
    // Treat the logo as a CIRCLE inscribed in its bounding box (radius = half its
    // larger side), not a square — so a round logo's transparent box corners don't
    // trip a false "past the safe area" warning when the visible artwork clearly fits.
    // Farthest extent from the tag centre = centre offset + that radius.
    const safeR = 0.5 * (1 - 2 * SAFE_INSET)
    const logoRadius = Math.max(fracW, fracH) / 2
    const centerOffset = Math.hypot(cx - 0.5, cy - 0.5)
    if (centerOffset + logoRadius > safeR) {
      warnings.push('Logo reaches past the round tag’s safe area and may be clipped at the edge.')
    }
  } else if (left < SAFE_INSET || right > 1 - SAFE_INSET || top < SAFE_INSET || bottom > 1 - SAFE_INSET) {
    warnings.push('Logo is close to the tag edge and may be trimmed. Keep it inside the dashed safe area.')
  }

  // ── Asset-type nudge (a gentle tip, not a warning) ──
  if (!isSvg) {
    tips.push('For the sharpest result, upload a vector logo (SVG) with a transparent background.')
  }

  return { errors, warnings, tips, dpi }
}
